import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Printer, X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { useBrand } from '@/shared/lib/brand';
import { currency, type CurrencyCode } from '@/shared/lib/format';

export interface OperatorReceiptData {
  customerName: string;
  uniqueId: string;
  organizationName: string;
  purchaseCode?: string;
  creditUsed: number;
  balance: number;
  date: Date;
}

interface OperatorReceiptProps {
  open: boolean;
  data: OperatorReceiptData | null;
  currencyCode: CurrencyCode;
  onClose: () => void;
}

type PrintVia = 1 | 2; // 1 = loja, 2 = cliente (igual balance_giftcard.ejs)

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function absoluteAssetUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/** HTML isolado do cupom — espelha o bloco `#imprimir` do balance_giftcard.ejs. */
function buildReceiptPrintHtml(
  data: OperatorReceiptData,
  opts: { logoUrl: string; creditLabel: string; balanceLabel: string; dateLabel: string },
): string {
  const name = escapeHtml(data.customerName);
  const giftcard = escapeHtml(data.uniqueId);
  const loja = escapeHtml(data.organizationName);
  const cupom = escapeHtml(data.purchaseCode || '—');
  const logo = escapeHtml(opts.logoUrl);

  return `
<div style="font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; max-width: 320px; margin: 0 auto; padding: 12px; font-size: 14px; line-height: 1.45;">
  <p style="text-align:center;margin:0 0 12px;">
    <img src="${logo}" alt="" width="64" height="64" style="display:inline-block;object-fit:contain;" />
  </p>
  <p style="margin:0 0 8px;">
    <strong>Nome:</strong> ${name}<br/>
    <strong>Giftcard:</strong> ${giftcard}<br/>
    <span>======================</span><br/>
    <strong>Movimentação Resgate</strong><br/>
    <span>======================</span><br/>
    <strong>Data:</strong> ${escapeHtml(opts.dateLabel)}<br/>
    <strong>Loja:</strong> ${loja}<br/>
    <strong>Código Cupom:</strong> ${cupom}<br/>
    <strong>Crédito Utilizado:</strong> ${escapeHtml(opts.creditLabel)}<br/>
    <span>======================</span><br/>
    <strong>Saldo: ${escapeHtml(opts.balanceLabel)}</strong><br/>
    <span>======================</span>
  </p>
  <p style="text-align:center;margin:16px 0 0;font-size:12px;">
    <strong>FIXE CRM</strong><br/>
    Plataforma de fidelidade<br/>
    e cartão presente<br/>
    fixecrm.com
  </p>
</div>`.trim();
}

/** Mesmo fluxo do legado: popup com HTML próprio + print (sem CSS da SPA). */
function printReceiptPopup(receiptHtml: string, via: PrintVia): boolean {
  const popup = window.open('', 'Imprimir', 'height=400,width=600');
  if (!popup) return false;

  const viaLabel = via === 1 ? '<p><i>Via loja</i></p>' : '<p><i>Via cliente</i></p>';
  popup.document.open();
  popup.document.write('<html><head><title>Imprimir</title></head><body>');
  popup.document.write(receiptHtml);
  popup.document.write(viaLabel);
  popup.document.write('</body></html>');
  popup.document.close();
  popup.focus();
  popup.print();
  return true;
}

export function OperatorReceipt({ open, data, currencyCode, onClose }: OperatorReceiptProps) {
  const { brand } = useBrand();

  if (!open || !data) return null;

  const fmt = (n: number) => currency(n, currencyCode);
  const dateLabel = data.date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const handlePrint = (via: PrintVia) => {
    const html = buildReceiptPrintHtml(data, {
      logoUrl: absoluteAssetUrl(brand.assets.icon.brand),
      creditLabel: fmt(data.creditUsed),
      balanceLabel: fmt(data.balance),
      dateLabel,
    });
    const ok = printReceiptPopup(html, via);
    if (!ok) {
      toast.error('Popup bloqueado. Permita pop-ups neste site para imprimir o cupom.');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Comprovante de resgate"
    >
      <div className="relative max-h-[calc(100dvh-2rem)] w-full max-w-[320px] overflow-y-auto rounded-2xl border border-[hsl(var(--glass-border)/0.5)] bg-card p-5 shadow-glass">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-accent"
          aria-label="Fechar"
        >
          <X className="size-4" />
        </button>

        <div className="space-y-3 text-sm leading-relaxed">
          <p className="text-center">
            <img
              src={brand.assets.icon.brand}
              alt={brand.displayName}
              width={64}
              height={64}
              className="mx-auto size-14 object-contain"
            />
          </p>

          <div className="space-y-1 border border-emerald-600/40 rounded-xl p-4">
            <p>
              <strong>Nome:</strong> {data.customerName}
            </p>
            <p>
              <strong>Giftcard:</strong> {data.uniqueId}
            </p>
            <p className="text-center tracking-widest text-muted-foreground">======================</p>
            <p className="font-semibold">Movimentação Resgate</p>
            <p className="text-center tracking-widest text-muted-foreground">======================</p>
            <p>
              <strong>Data:</strong> {dateLabel}
            </p>
            <p>
              <strong>Loja:</strong> {data.organizationName}
            </p>
            <p>
              <strong>Código Cupom:</strong> {data.purchaseCode || '—'}
            </p>
            <p>
              <strong>Crédito Utilizado:</strong> {fmt(data.creditUsed)}
            </p>
            <p className="text-center tracking-widest text-muted-foreground">======================</p>
            <p>
              <strong>Saldo: {fmt(data.balance)}</strong>
            </p>
            <p className="text-center tracking-widest text-muted-foreground">======================</p>
          </div>

          <div className="pt-2 text-center text-xs text-muted-foreground">
            <p className="font-semibold text-foreground">FIXE CRM</p>
            <p>Plataforma de fidelidade</p>
            <p>e cartão presente</p>
            <p className="mt-1 font-medium text-primary">fixecrm.com</p>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Button type="button" className="h-11 w-full gap-2" onClick={() => handlePrint(2)}>
            <Printer className="size-4" />
            Imprimir via Cliente
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-11 w-full gap-2"
            onClick={() => handlePrint(1)}
          >
            <Printer className="size-4" />
            Imprimir via Loja
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
